import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import api from '../../utils/api';
import { useAuth } from '../../App';
import { Loader2, Percent, CalendarRange, Plus, Trash2, Pencil, Play, X } from 'lucide-react';
import { toast } from 'sonner';

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

const EMPTY_CAL = { name: '', year: new Date().getFullYear() + 1, region: '', city: '', bands: [] };
const EMPTY_BULK = { platform: 'hw', markup_percent: '', region: '', city: '', year: '', arrival_from: '', arrival_to: '', band: '' };

export default function PricingManagerPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'customer_service';
  const [activeTab, setActiveTab] = useState('ricarichi');
  const [platforms, setPlatforms] = useState([]);
  const [bands, setBands] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);

  // Bulk markup
  const [bulk, setBulk] = useState(EMPTY_BULK);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Calendars
  const [calendars, setCalendars] = useState([]);
  const [calLoading, setCalLoading] = useState(true);
  const [calDialog, setCalDialog] = useState(false);
  const [editCal, setEditCal] = useState(null);
  const [calForm, setCalForm] = useState(EMPTY_CAL);
  const [calSaving, setCalSaving] = useState(false);
  const [genDialog, setGenDialog] = useState(null); // calendar being generated
  const [genSourceYear, setGenSourceYear] = useState(new Date().getFullYear());
  const [genLoading, setGenLoading] = useState(false);
  const [genReport, setGenReport] = useState(null);

  const fetchCalendars = useCallback(async () => {
    setCalLoading(true);
    try {
      const res = await api.get('/pricing/destination-calendars');
      setCalendars(res.data.calendars || []);
    } catch { toast.error('Errore caricamento calendari'); }
    finally { setCalLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, dRes] = await Promise.all([
          api.get('/pricing/platforms'),
          api.get('/pricing/destinations'),
        ]);
        setPlatforms(pRes.data.platforms || []);
        setBands(pRes.data.bands || []);
        setRegions(dRes.data.regions || []);
        setCities(dRes.data.cities || []);
      } catch { /* silente */ }
    })();
    fetchCalendars();
  }, [fetchCalendars]);

  // ─── Bulk markup handlers ───
  const buildBulkPayload = (dryRun) => {
    const p = { platform: bulk.platform, markup_percent: Number(bulk.markup_percent), dry_run: dryRun };
    if (bulk.region) p.region = bulk.region;
    if (bulk.city) p.city = bulk.city;
    if (bulk.year !== '') p.year = Number(bulk.year);
    if (bulk.arrival_from) p.arrival_from = bulk.arrival_from;
    if (bulk.arrival_to) p.arrival_to = bulk.arrival_to;
    if (bulk.band) p.band = bulk.band;
    return p;
  };
  const runBulk = async (dryRun) => {
    if (bulk.markup_percent === '' || Number(bulk.markup_percent) < 0) { toast.error('Indica la percentuale di ricarico'); return; }
    if (!bulk.region && !bulk.city) { toast.error('Indica almeno regione o città'); return; }
    if (!dryRun && !window.confirm(`Applicare il ricarico ${bulk.markup_percent}% (${bulk.platform}) a tutti i periodi selezionati? L'operazione sovrascrive i ricarichi esistenti per questa piattaforma.`)) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/pricing/bulk-markup', buildBulkPayload(dryRun));
      if (dryRun) {
        setBulkPreview(res.data);
      } else {
        toast.success(res.data.message);
        setBulkPreview(null);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore applicazione ricarico'); }
    finally { setBulkLoading(false); }
  };

  // ─── Calendar handlers ───
  const openCalDialog = (cal = null) => {
    setEditCal(cal);
    setCalForm(cal ? { name: cal.name, year: cal.year, region: cal.region || '', city: cal.city || '', bands: (cal.bands || []).map(b => ({ ...b })) } : { ...EMPTY_CAL, bands: [] });
    setCalDialog(true);
  };
  const setBand = (i, k, v) => setCalForm(f => ({ ...f, bands: f.bands.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const addBand = () => setCalForm(f => ({ ...f, bands: [...f.bands, { start: '', end: '', band: 'A' }] }));
  const removeBand = (i) => setCalForm(f => ({ ...f, bands: f.bands.filter((_, idx) => idx !== i) }));

  const saveCalendar = async (e) => {
    e.preventDefault();
    setCalSaving(true);
    try {
      const payload = { name: calForm.name, year: Number(calForm.year), region: calForm.region || null, city: calForm.city || null, bands: calForm.bands };
      if (editCal) await api.put(`/pricing/destination-calendars/${editCal.id}`, payload);
      else await api.post('/pricing/destination-calendars', payload);
      toast.success(editCal ? 'Calendario aggiornato' : 'Calendario creato');
      setCalDialog(false); setEditCal(null);
      fetchCalendars();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore salvataggio calendario'); }
    finally { setCalSaving(false); }
  };
  const deleteCalendar = async (cal) => {
    if (!window.confirm(`Eliminare il calendario "${cal.name}"? I listini già generati non verranno toccati.`)) return;
    try { await api.delete(`/pricing/destination-calendars/${cal.id}`); toast.success('Calendario eliminato'); fetchCalendars(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const runGenerate = async () => {
    setGenLoading(true);
    setGenReport(null);
    try {
      const res = await api.post(`/pricing/destination-calendars/${genDialog.id}/generate-pricelists`, { source_year: Number(genSourceYear) });
      setGenReport(res.data);
      toast.success(`Listini generati: ${res.data.periods_created} periodi per ${res.data.properties_processed} immobili`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore generazione listini'); }
    finally { setGenLoading(false); }
  };

  return (
    <Layout title="Listini & Ricarichi">
      <div className="space-y-4" data-testid="pricing-manager-page">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 max-w-md" data-testid="pricing-tabs">
          {[{ id: 'ricarichi', label: 'Ricarichi in blocco' }, { id: 'calendari', label: 'Calendari destinazione' }].map(t => (
            <button key={t.id} data-testid={`pricing-tab-${t.id}`} onClick={() => setActiveTab(t.id)}
              className={`flex-1 whitespace-nowrap px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══ RICARICHI IN BLOCCO ═══ */}
        {activeTab === 'ricarichi' && (
          <Card className="p-5 space-y-4" data-testid="pricing-bulk-card">
            <div>
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Percent className="h-4 w-4 text-blue-600" /> Ricarico percentuale in blocco per piattaforma
              </h3>
              <p className="text-xs text-slate-500 mt-1">Applica una % di ricarico ai listini di tutti gli immobili di una destinazione, per una singola piattaforma. Per il nostro sito (HW) il prezzo giornaliero viene ricalcolato dal pagato al proprietario.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Piattaforma *">
                <select value={bulk.platform} onChange={(e) => setBulk(b => ({ ...b, platform: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="bulk-platform">
                  {platforms.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Ricarico (%) *">
                <input type="number" step="0.01" min="0" max="500" value={bulk.markup_percent}
                  onChange={(e) => setBulk(b => ({ ...b, markup_percent: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Es. 56" data-testid="bulk-percent" />
              </Field>
              <Field label="Regione">
                <select value={bulk.region} onChange={(e) => setBulk(b => ({ ...b, region: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="bulk-region">
                  <option value="">Tutte / non filtrare</option>
                  {regions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Città">
                <input type="text" list="pricing-cities" value={bulk.city} onChange={(e) => setBulk(b => ({ ...b, city: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Es. Genova" data-testid="bulk-city" />
                <datalist id="pricing-cities">{cities.map(c => <option key={c} value={c} />)}</datalist>
              </Field>
              <Field label="Anno listino">
                <input type="number" value={bulk.year} onChange={(e) => setBulk(b => ({ ...b, year: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder={String(new Date().getFullYear())} data-testid="bulk-year" />
              </Field>
              <Field label="Arrivi dal">
                <input type="date" value={bulk.arrival_from} onChange={(e) => setBulk(b => ({ ...b, arrival_from: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="bulk-from" />
              </Field>
              <Field label="Arrivi fino al">
                <input type="date" value={bulk.arrival_to} onChange={(e) => setBulk(b => ({ ...b, arrival_to: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="bulk-to" />
              </Field>
              <Field label="Fascia">
                <select value={bulk.band} onChange={(e) => setBulk(b => ({ ...b, band: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="bulk-band">
                  <option value="">Tutte</option>
                  {bands.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => runBulk(true)} disabled={bulkLoading || !canEdit} data-testid="bulk-preview-btn">
                {bulkLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Anteprima (senza modifiche)
              </Button>
              <Button onClick={() => runBulk(false)} disabled={bulkLoading || !canEdit} data-testid="bulk-apply-btn">
                {bulkLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Percent className="h-4 w-4 mr-1" />} Applica ricarico
              </Button>
              {bulkPreview && (
                <span className="text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5" data-testid="bulk-preview-result">
                  Verranno aggiornati <strong>{bulkPreview.matched_periods}</strong> periodi di <strong>{bulkPreview.affected_properties}</strong> immobili
                  <button onClick={() => setBulkPreview(null)} className="ml-2 text-slate-400 hover:text-slate-600"><X className="h-3 w-3 inline" /></button>
                </span>
              )}
            </div>
          </Card>
        )}

        {/* ═══ CALENDARI DESTINAZIONE ═══ */}
        {activeTab === 'calendari' && (
          <Card className="p-5 space-y-4" data-testid="pricing-calendars-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <CalendarRange className="h-4 w-4 text-blue-600" /> Calendari destinazione
                </h3>
                <p className="text-xs text-slate-500 mt-1">Definisci le fasce (A-F) dell'anno per una destinazione e genera in blocco i listini dell'anno successivo usando i prezzi per fascia dell'anno precedente.</p>
              </div>
              {canEdit && <Button size="sm" onClick={() => openCalDialog()} data-testid="cal-add-btn"><Plus className="h-4 w-4 mr-1" /> Nuovo calendario</Button>}
            </div>
            {calLoading ? (
              <div className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
            ) : calendars.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm" data-testid="cal-empty">
                <CalendarRange className="h-8 w-8 mx-auto mb-2" /> Nessun calendario destinazione — creane uno per iniziare
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left p-2 font-medium text-slate-600">Nome</th>
                    <th className="text-center p-2 font-medium text-slate-600">Anno</th>
                    <th className="text-left p-2 font-medium text-slate-600">Destinazione</th>
                    <th className="text-center p-2 font-medium text-slate-600">Fasce</th>
                    <th className="text-right p-2"></th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {calendars.map(cal => (
                      <tr key={cal.id} className="hover:bg-slate-50" data-testid={`cal-row-${cal.id}`}>
                        <td className="p-2 font-medium">{cal.name}</td>
                        <td className="p-2 text-center">{cal.year}</td>
                        <td className="p-2">{[cal.city, cal.region].filter(Boolean).join(' · ') || '-'}</td>
                        <td className="p-2 text-center">
                          <div className="flex gap-1 justify-center flex-wrap">
                            {(cal.bands || []).map((b, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold" title={`${b.start} → ${b.end}`}>{b.band}</span>
                            ))}
                            {(cal.bands || []).length === 0 && <span className="text-slate-300">-</span>}
                          </div>
                        </td>
                        <td className="p-2 text-right whitespace-nowrap">
                          {canEdit && (<>
                            <button onClick={() => { setGenDialog(cal); setGenSourceYear(cal.year - 1); setGenReport(null); }} className="p-1.5 text-slate-400 hover:text-emerald-600" title="Genera listini" data-testid={`cal-generate-${cal.id}`}><Play className="h-3.5 w-3.5" /></button>
                            <button onClick={() => openCalDialog(cal)} className="p-1.5 text-slate-400 hover:text-blue-600" title="Modifica" data-testid={`cal-edit-${cal.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                            <button onClick={() => deleteCalendar(cal)} className="p-1.5 text-slate-400 hover:text-red-600" title="Elimina" data-testid={`cal-delete-${cal.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          </>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Dialog calendario */}
        <Dialog open={calDialog} onOpenChange={(o) => { if (!o) { setCalDialog(false); setEditCal(null); } }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cal-dialog">
            <DialogHeader>
              <DialogTitle>{editCal ? 'Modifica calendario' : 'Nuovo calendario destinazione'}</DialogTitle>
              <DialogDescription>Fasce dell'anno per la destinazione (usate per generare i listini)</DialogDescription>
            </DialogHeader>
            <form onSubmit={saveCalendar} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nome *">
                  <input type="text" value={calForm.name} onChange={(e) => setCalForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" required placeholder="Es. Liguria 2027" data-testid="cal-form-name" />
                </Field>
                <Field label="Anno di destinazione *">
                  <input type="number" value={calForm.year} onChange={(e) => setCalForm(f => ({ ...f, year: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" required data-testid="cal-form-year" />
                </Field>
                <Field label="Regione">
                  <select value={calForm.region} onChange={(e) => setCalForm(f => ({ ...f, region: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="cal-form-region">
                    <option value="">-</option>
                    {regions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Città">
                  <input type="text" list="pricing-cities-cal" value={calForm.city} onChange={(e) => setCalForm(f => ({ ...f, city: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Es. Genova" data-testid="cal-form-city" />
                  <datalist id="pricing-cities-cal">{cities.map(c => <option key={c} value={c} />)}</datalist>
                </Field>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-slate-600">Fasce dell'anno</label>
                  <Button type="button" size="sm" variant="outline" onClick={addBand} data-testid="cal-band-add-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi fascia</Button>
                </div>
                {calForm.bands.length === 0 ? (
                  <p className="text-xs text-slate-400 py-3 text-center border rounded-lg">Nessuna fascia — aggiungi i periodi dell'anno {calForm.year}</p>
                ) : (
                  <div className="space-y-2">
                    {calForm.bands.map((b, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_90px_36px] gap-2 items-center" data-testid={`cal-band-row-${i}`}>
                        <input type="date" value={b.start} onChange={(e) => setBand(i, 'start', e.target.value)} className="px-3 py-2 border rounded-lg text-sm" required data-testid={`cal-band-start-${i}`} />
                        <input type="date" value={b.end} onChange={(e) => setBand(i, 'end', e.target.value)} className="px-3 py-2 border rounded-lg text-sm" required data-testid={`cal-band-end-${i}`} />
                        <select value={b.band} onChange={(e) => setBand(i, 'band', e.target.value)} className="px-2 py-2 border rounded-lg text-sm bg-white" data-testid={`cal-band-band-${i}`}>
                          {bands.map(bd => <option key={bd} value={bd}>{bd}</option>)}
                        </select>
                        <button type="button" onClick={() => removeBand(i)} className="p-2 text-slate-400 hover:text-red-600" data-testid={`cal-band-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" size="sm" onClick={() => { setCalDialog(false); setEditCal(null); }}>Annulla</Button>
                <Button type="submit" size="sm" disabled={calSaving} data-testid="cal-form-submit">
                  {calSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  {editCal ? 'Salva modifiche' : 'Crea calendario'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog generazione listini */}
        <Dialog open={!!genDialog} onOpenChange={(o) => { if (!o) { setGenDialog(null); setGenReport(null); } }}>
          <DialogContent className="max-w-lg" data-testid="cal-generate-dialog">
            <DialogHeader>
              <DialogTitle>Genera listini {genDialog?.year}</DialogTitle>
              <DialogDescription>
                Calendario "{genDialog?.name}" — verranno creati i periodi {genDialog?.year} per gli immobili di {[genDialog?.city, genDialog?.region].filter(Boolean).join(' · ')} usando i prezzi per fascia dell'anno sorgente. I periodi generati in precedenza da calendario verranno sostituiti.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Field label="Anno sorgente dei prezzi *">
                <input type="number" value={genSourceYear} onChange={(e) => setGenSourceYear(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="cal-gen-source-year" />
              </Field>
              {genReport && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm" data-testid="cal-gen-report">
                  <p className="font-semibold text-emerald-800">Generati {genReport.periods_created} periodi per {genReport.properties_processed} immobili</p>
                  {(genReport.skipped || []).length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-amber-700">Avvisi ({genReport.skipped.length}):</p>
                      <ul className="text-xs text-amber-700 mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                        {genReport.skipped.map((s, i) => <li key={i}>• {s.code || s.property}: {s.reason}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" size="sm" onClick={() => { setGenDialog(null); setGenReport(null); }}>Chiudi</Button>
                <Button size="sm" onClick={runGenerate} disabled={genLoading} data-testid="cal-gen-run-btn">
                  {genLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />} Genera listini
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
